import { Stakeable } from "@modules/staking/domain/entities/stakeable.model";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { StakingProcess } from "@modules/staking/domain/entities/staking-process.model";
import { TradeAmountType } from "@modules/shared/domain/entities/trade-amount-type.model";
import { convertTradableToFiat } from "@helpers/tradable-convert";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";

import { Tradable } from "@models/interfaces/tradable";
import { Fiat } from "@models";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { FiatService } from "@main-services/currency";
import { AccountService } from "@modules/account/services/account.service";
import {
	GetStakingBannerInfoQuery,
	GetStakingFilteredAssetsQuery,
	GetStakingOverviewQuery,
	GetStakingRewardsQuery,
	GetStakingRewardTransactionsQuery,
	StakingActionInput,
	StakingType
} from "@gql/staking";
import { StakeableAvailability } from "@modules/staking/domain/entities/stakable-availability.model";
import { TradableWalletService } from "@main-services/wallet/tradable_wallet.service";
import { TradableTransaction } from "@models/transaction";
import { ConvertFiatToTradablePipe } from "@modules/core/pipes";
import { Injectable } from "@angular/core";
import { StakingOverviewState } from "@modules/staking/domain/entities/staking-overview-state.model";
import { StakingOverviewStateAdapter } from "@modules/staking/domain/entities/staking-overview-adapter";
import { StakingRewardTransactionAdapter } from "@modules/staking/domain/entities/staking-reward-transaction-adapter.model";
import { StakingRewardsState } from "@modules/staking/domain/entities/staking-rewards-state.model";
import { StakingBannerState } from "@modules/staking/domain/entities/staking-banner-state.model";
import { StakingBannerStateAdapter } from "@modules/staking/domain/entities/staking-banner-adapter";
import { GetStakeableDataByIdQuery } from "../../../../../api/graphql/staking/generated/staking.stakeable-info-by-id.service.graphql-gen";
import { StakingAdpAdapter } from "@modules/staking/domain/entities/staking-adp-adapter";
import { PortfolioAssetDetails } from "@modules/portfolio/domain/entities/portfolio-overview.model";

@Injectable()
export class StakingHelperService {
	constructor(
		private readonly tradableWalletService: TradableWalletListService,
		private readonly tradableListService: TradableListService,
		private readonly fiatService: FiatService,
		private readonly accountService: AccountService,
		private readonly convertFiatToTradablePipe: ConvertFiatToTradablePipe
	) {}

	public getParsedStakeables(stakeInfo: GetStakingFilteredAssetsQuery): Stakeable[] {
		return stakeInfo.assetConfig.assets.map((item) => this.parseStakeable(item, stakeInfo));
	}

	public processStakeableInfo(stakebleInfo: GetStakeableDataByIdQuery): Stakeable {
		const stakeableInfoAdapter: StakingAdpAdapter = new StakingAdpAdapter(this.tradableListService);
		return stakeableInfoAdapter.adapt(stakebleInfo);
	}

	public parseStakeable(
		item: {
			assetId: string;
			availability: string;
			yearlyInterest: number;
			minStakingAmount: { value: string };
		},
		filteredAsset: GetStakingFilteredAssetsQuery
	): Stakeable {
		const matchedAsset = filteredAsset.assetConfig.assets.find((asset) => asset.assetId === item.assetId);
		const asset: Tradable = this.tradableListService.getItemById(item.assetId);
		return {
			id: item.assetId,
			stakingMode: matchedAsset.stakingMode,
			availability: item.availability as StakeableAvailability,
			yearlyInterest: item.yearlyInterest,
			name: asset?.getName() || "",
			symbol: asset?.getSymbol() || "",
			stakedAmount: "",
			rewardsAmount: {
				totalCurrent: {
					fiat: "0",
					asset: "0"
				},
				totalLastMonth: {
					fiat: "0",
					asset: "0"
				}
			},
			rewardIntervals: matchedAsset.rewardIntervalInDays,
			nextReward: matchedAsset.nextReward,
			minStakingAmount: item.minStakingAmount?.value,
			isLocked: matchedAsset.stakingType === StakingType.Locked,
			unboundPeriod: matchedAsset.unboundPeriod,
			autoStakingEnabled: matchedAsset?.isAutoStakingEnabled
		};
	}

	public parseRewardTransactions(item: GetStakingRewardTransactionsQuery): StakingRewardsState {
		const rewardAdapter: StakingRewardTransactionAdapter = new StakingRewardTransactionAdapter(
			this.tradableListService
		);
		return rewardAdapter.adapt(item);
	}

	public getWalletService(walletId: string): TradableWalletService<TradableWallet, TradableTransaction, Tradable> {
		return this.tradableWalletService.serviceForId(walletId);
	}

	public getStakeActionInput(stakingProcess: StakingProcess): StakingActionInput {
		return {
			assetAmount: {
				value:
					stakingProcess.amountType === TradeAmountType.ASSET
						? stakingProcess.amount
						: "" +
							this.convertFiatToTradablePipe.transform(stakingProcess.amount, stakingProcess.stakeable.id)
			},
			assetId: stakingProcess.stakeable.id,
			walletId: stakingProcess.wallet.getId(),
			type: stakingProcess.stakeable.isLocked ? StakingType.Locked : StakingType.Flexible
		};
	}

	public updateStakeable(stakeable: Stakeable, stakingInfo: GetStakingRewardsQuery): Stakeable {
		const matchedAsset = stakingInfo.assetConfig.assets.find((item) => item.assetId === stakeable.id);
		const stakedAmount: string =
			stakingInfo.balances.find((item) => item.asset.assetId === stakeable.id)?.stakedAmount.value || "";
		const rewardIntervals: number = matchedAsset.rewardIntervalInDays;
		const nextReward = matchedAsset.nextReward;
		return { ...stakeable, stakedAmount, rewardIntervals, nextReward };
	}
	public adaptOverviewResponse(
		item: GetStakingOverviewQuery,
		ownedAssets?: PortfolioAssetDetails[]
	): StakingOverviewState {
		const overviewAdapter: StakingOverviewStateAdapter = new StakingOverviewStateAdapter(
			this.tradableListService,
			this.accountService
		);
		return overviewAdapter.adapt(item, ownedAssets);
	}

	public adaptBannerResponse(item: GetStakingBannerInfoQuery): StakingBannerState {
		const bannerAdapter: StakingBannerStateAdapter = new StakingBannerStateAdapter(
			this.tradableListService,
			this.accountService
		);
		return bannerAdapter.adapt(item);
	}

	// ----------> Utility Functions, not staking-specific

	public getTradable(process: StakingProcess): Tradable {
		return this.getSelectedTradable(process.stakeable.id);
	}

	public getFiatValue(process: StakingProcess, amountInTradable: number): number {
		return +convertTradableToFiat(this.getTradable(process), amountInTradable, this.getFiat()).toFixed(2);
	}

	public getSelectedTradable(id: string): Tradable {
		return this.tradableListService.getItemById(id);
	}

	public getWalletsByStakeableId(id: string): TradableWallet[] {
		return this.tradableWalletService.getWalletsByTradableId(id);
	}

	public getFiat(id?: string): Fiat {
		if (id !== undefined) {
			return this.fiatService.getItemById(id);
		}
		return this.fiatService.getItemById(this.accountService.account.getFiatId());
	}
}
